import type {
  GetParameterRequest,
  GetParametersRequest,
} from '@aws-sdk/client-ssm';
import { ReebeloStoreT, SSMBucket } from '@lambda/reebelo';
import LRUCache from 'lru-cache';
import { Logger } from '../../logging/logger';

// `@aws-sdk/client-ssm` is loaded lazily via dynamic import so it — and its
// Node polyfills (crypto-browserify, stream-http, stream-browserify) — never
// enter client bundles that merely hold an SSM instance (e.g. CartApi, which
// does `this.ssm = new SSM()` in its constructor). The SDK only loads at
// runtime when an SSM read actually executes, which is server-side only.
type SsmModule = typeof import('@aws-sdk/client-ssm');

type SsmClientInstance = InstanceType<SsmModule['SSMClient']>;

let modulePromise: Promise<SsmModule> | null = null;

const loadSsmModule = (): Promise<SsmModule> => {
  if (!modulePromise) {
    modulePromise = import('@aws-sdk/client-ssm');
  }

  return modulePromise;
};

let defaultClient: SsmClientInstance | null = null;
const regionClients = new Map<string, SsmClientInstance>();

const getDefaultClient = async (): Promise<SsmClientInstance> => {
  if (!defaultClient) {
    const { SSMClient: Client } = await loadSsmModule();

    defaultClient = new Client();
  }

  return defaultClient;
};

const getSSMClientForRegion = async (
  region: string,
): Promise<SsmClientInstance> => {
  const cached = regionClients.get(region);

  if (cached) {
    return cached;
  }

  const { SSMClient: Client } = await loadSsmModule();
  const client = new Client({ region });

  regionClients.set(region, client);

  return client;
};

const lruCache = new LRUCache<string, any>({
  max: 500,
  ttl: 1000 * 60 * 5,
});

const racingConditionResolve = (cacheKey: string): Promise<void> =>
  new Promise((resolve) => {
    let count = 0;

    const checkCache = () => {
      if (!lruCache.has(cacheKey) || count >= 10) {
        resolve();
      } else {
        count += 1;
        setTimeout(checkCache, 100);
      }
    };

    checkCache();
  });

export default class SSM {
  logger: Logger;

  constructor() {
    this.logger = new Logger('ssm');
  }

  async getValue<T>(
    key: string,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    try {
      const name = `${key}`.toLowerCase();
      const racingKey = `racing: ${name}`;

      this.logger.info({ key, name }, 'getting SSM parameter');

      if (process.env[key]) {
        this.logger.warn({ key, name }, 'returning local env variable');

        return process.env[key] as T;
      }

      if (cacheEnabled) {
        if (lruCache.has(name)) {
          this.logger.info(`Config getValue hit cache: ${name}`);

          return lruCache.get(name) ?? null;
        }

        await racingConditionResolve(racingKey);

        if (lruCache.has(key)) {
          this.logger.info(`Config getValue hit cache after racing: ${name}`);

          return lruCache.get(key) ?? null;
        }

        lruCache.set(racingKey, 1, { ttl: 1000 });
      }

      const params: GetParameterRequest = {
        Name: name,
        WithDecryption: true,
      };

      const { GetParameterCommand } = await loadSsmModule();
      const client = await getDefaultClient();
      const response = await client.send(new GetParameterCommand(params));

      if (response.Parameter && response.Parameter.Value) {
        if (cacheEnabled) {
          this.logger.info(`Config getValue set cache: ${name}`);
          lruCache.set(key, response.Parameter.Value, { ttl });
          lruCache.delete(racingKey);
        }

        return response.Parameter.Value as T;
      }

      throw new Error(`Parameter "${name}" not found`);
    } catch (e: any) {
      this.logger.error({ e, key }, 'error getting SSM parameter');

      return defaultValue;
    }
  }

  async getStoreValue<T>(
    store: SSMBucket,
    key: string,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    const name = `/${store}/${key}`.toLowerCase();

    return this.getValue(name, defaultValue, cacheEnabled, ttl);
  }

  async getStoreValues<T>(
    bucket: SSMBucket,
    keys: string[],
  ): Promise<{ [key: string]: T } | null> {
    try {
      const names = keys.map((key) => `/${bucket}/${key}`.toLowerCase());

      const params: GetParametersRequest = {
        Names: names,
        WithDecryption: true,
      };

      const { GetParametersCommand } = await loadSsmModule();
      const client = await getDefaultClient();
      const response = await client.send(new GetParametersCommand(params));
      const values: { [key: string]: T } = {};

      response.Parameters?.forEach((param) => {
        const paramName = param.Name?.split('/').pop();

        if (paramName) {
          values[paramName] = param.Value as T;
        }
      });

      if (Object.keys(values).length > 0) {
        return values;
      }

      throw new Error(`Parameters not found`);
    } catch (e: any) {
      this.logger.error({ e, keys, bucket }, 'error getting SSM parameters');

      return null;
    }
  }

  async getValueByRegion<T>(
    key: string,
    store: ReebeloStoreT,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    try {
      const isNA = [
        'reebelo-us',
        'reebelo-ca',
        'reebelo-uk',
        'reebelo-fr',
        'reebelo-de',
        'reebelo-es',
        'reebelo-it',
        'reebelo-se',
        'reebelo-ie',
        'reebelo-nl',
        'reebelo-at',
        'reebelo-be',
        'reebelo-pt',
        'reebelo-fi',
      ].includes(store);
      const region = isNA ? 'us-west-1' : 'ap-southeast-1';
      const ssmClient = await getSSMClientForRegion(region);
      const name = `${key}`.toLowerCase();
      const racingKey = `racing: ${name}`;

      this.logger.info({ key, name }, 'getting SSM parameter');

      const params: GetParameterRequest = {
        Name: name,
        WithDecryption: true,
      };

      const { GetParameterCommand } = await loadSsmModule();
      const response = await ssmClient.send(new GetParameterCommand(params));

      if (response.Parameter && response.Parameter.Value) {
        if (cacheEnabled) {
          this.logger.info(`Skip setting value in cache: ${name}`);
        }

        return response.Parameter.Value as T;
      }

      throw new Error(`Parameter "${name}" not found`);
    } catch (e: any) {
      this.logger.error({ e, key }, 'error getting SSM parameter');

      return defaultValue;
    }
  }
}
